import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, LogOut, Loader2, Save } from "lucide-react";
import BrandLogo from "@/components/BrandLogo.jsx";
import { authedAxios, formatApiErrorDetail } from "./api.js";
import { TextInput, TextArea, ImageInput, ListEditor } from "./fields.jsx";
import { mediaUrl } from "@/content/ContentContext.jsx";

const TABS = ["Brand & Logo", "Hero", "Marquee", "About", "Facilities", "Gallery", "Booking", "Location & Contact", "Footer"];

export const AdminDashboard = ({ user, onLogout }) => {
  const [draft, setDraft] = useState(null);
  const [tab, setTab] = useState("Hero");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    authedAxios
      .get("/content")
      .then(({ data }) => setDraft(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);

  const sec = (name) => draft?.[name] || {};
  const setSec = (name) => (patch) => setDraft((d) => ({ ...d, [name]: { ...d[name], ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await authedAxios.put("/content", draft);
      setDraft(data);
      toast.success("Website content updated — refresh the site to see it live.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const logout = async () => {
    try {
      await authedAxios.post("/auth/logout");
    } finally {
      onLogout();
    }
  };

  if (!draft) {
    return (
      <div className="flex min-h-screen items-center justify-center text-zinc-400" data-testid="admin-draft-loading">
        <Loader2 size={22} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-7xl flex-col px-4 py-6 sm:px-8" data-testid="admin-dashboard">
      <header className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-zinc-200 bg-white px-5 py-4">
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center">
            <BrandLogo size={40} rounded="rounded-xl" fallbackBg="bg-brand" testid="admin-header-logo" />
          </span>
          <div>
            <h1 className="font-display text-lg text-brand">Website Manager</h1>
            <p className="text-xs text-zinc-500" data-testid="admin-user-email">{user.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-brand/25 px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-brand hover:bg-brand-tint"
            data-testid="admin-view-site"
          >
            <ExternalLink size={14} /> View Site
          </a>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-all hover:bg-brand-deep disabled:opacity-60"
            data-testid="admin-save-button"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
            {saving ? "Saving…" : "Save Changes"}
          </button>
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-full border border-zinc-200 px-4 py-2 text-xs font-semibold text-zinc-500 transition-colors hover:border-red-300 hover:text-red-500"
            data-testid="admin-logout-button"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <nav className="mt-6 flex flex-wrap gap-2" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-full px-5 py-2 text-xs font-semibold uppercase tracking-[0.12em] transition-all ${
              tab === t ? "bg-brand text-white shadow-[0_8px_20px_rgba(13,91,49,0.25)]" : "border border-zinc-200 bg-white text-zinc-500 hover:text-brand"
            }`}
            data-testid={`admin-tab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          >
            {t}
          </button>
        ))}
      </nav>

      <div className="mt-6 flex-1 rounded-2xl border border-zinc-200 bg-white p-6 sm:p-8" data-testid="admin-panel">
        {tab === "Brand & Logo" && (
          <div className="grid max-w-3xl gap-5">
            <ImageInput label="Website Logo" value={sec("brand").logo || ""} onChange={(v) => setSec("brand")({ logo: v })} testid="admin-brand-logo" />
            <p className="text-xs leading-relaxed text-zinc-400" data-testid="admin-brand-hint">
              Shown in the header, footer, hero badge and as the browser favicon. Leave empty to use the
              default Angukumaran emblem. A PNG or WebP with transparent background is recommended.
            </p>
          </div>
        )}

        {tab === "Hero" && (
          <div className="grid gap-5 md:grid-cols-2">
            <TextInput label="Eyebrow (location line)" value={sec("hero").eyebrow || ""} onChange={(v) => setSec("hero")({ eyebrow: v })} testid="admin-hero-eyebrow" />
            <TextInput label="Tamil Name" value={sec("hero").tamil || ""} onChange={(v) => setSec("hero")({ tamil: v })} testid="admin-hero-tamil" />
            <TextInput label="Headline — Line 1" value={sec("hero").titleLine1 || ""} onChange={(v) => setSec("hero")({ titleLine1: v })} testid="admin-hero-title1" />
            <div className="grid grid-cols-2 gap-4">
              <TextInput label="Headline — Accent Word (italic green)" value={sec("hero").titleAccent || ""} onChange={(v) => setSec("hero")({ titleAccent: v })} testid="admin-hero-accent" />
              <TextInput label="Headline — After Accent" value={sec("hero").titleRest || ""} onChange={(v) => setSec("hero")({ titleRest: v })} testid="admin-hero-title2" />
            </div>
            <div className="md:col-span-2">
              <TextArea label="Subline" value={sec("hero").subline || ""} onChange={(v) => setSec("hero")({ subline: v })} testid="admin-hero-subline" />
            </div>
            <div className="md:col-span-2">
              <ImageInput label="Hero Image" value={sec("hero").image || ""} onChange={(v) => setSec("hero")({ image: v })} testid="admin-hero-image" />
            </div>
          </div>
        )}

        {tab === "Marquee" && (
          <ListEditor
            label="Scrolling Marquee Items"
            items={sec("marquee").items || []}
            onChange={(items) => setSec("marquee")({ items })}
            newItem={() => ({ text: "New Item" })}
            testid="admin-marquee"
            renderItem={(item, patch) => (
              <TextInput label="Text" value={item.text || ""} onChange={(v) => patch({ text: v })} testid="admin-marquee-text" />
            )}
          />
        )}

        {tab === "About" && (
          <div className="space-y-6">
            <div className="grid gap-5 md:grid-cols-2">
              <TextInput label="Eyebrow" value={sec("about").eyebrow || ""} onChange={(v) => setSec("about")({ eyebrow: v })} testid="admin-about-eyebrow" />
              <TextInput label="Title" value={sec("about").title || ""} onChange={(v) => setSec("about")({ title: v })} testid="admin-about-title" />
            </div>
            <TextArea label="Lead Paragraph" value={sec("about").lead || ""} onChange={(v) => setSec("about")({ lead: v })} testid="admin-about-lead" />
            <TextArea label="Body Paragraph" value={sec("about").body || ""} onChange={(v) => setSec("about")({ body: v })} testid="admin-about-body" />
            <ListEditor
              label="Experience Cards"
              items={sec("about").cards || []}
              onChange={(cards) => setSec("about")({ cards })}
              newItem={() => ({ tag: "New", title: "New Card", desc: "Description", icon: "Sparkles", image: "" })}
              testid="admin-about-cards"
              renderItem={(card, patch) => (
                <div className="grid gap-4 md:grid-cols-2">
                  <TextInput label="Tag" value={card.tag} onChange={(v) => patch({ tag: v })} testid="admin-about-card-tag" />
                  <TextInput label="Icon (Users / Cake / Clapperboard / Video / Sparkles)" value={card.icon} onChange={(v) => patch({ icon: v })} testid="admin-about-card-icon" />
                  <TextInput label="Title" value={card.title} onChange={(v) => patch({ title: v })} testid="admin-about-card-title" />
                  <TextArea label="Description" value={card.desc} onChange={(v) => patch({ desc: v })} rows={2} testid="admin-about-card-desc" />
                  <div className="md:col-span-2">
                    <ImageInput label="Card Image (leave empty for icon card)" value={card.image} onChange={(v) => patch({ image: v })} testid="admin-about-card-image" />
                  </div>
                </div>
              )}
            />
          </div>
        )}

        {tab === "Facilities" && (
          <div className="space-y-6">
            <div className="grid gap-5 md:grid-cols-2">
              <TextInput label="Eyebrow" value={sec("facilities").eyebrow || ""} onChange={(v) => setSec("facilities")({ eyebrow: v })} testid="admin-fac-eyebrow" />
              <TextInput label="Title" value={sec("facilities").title || ""} onChange={(v) => setSec("facilities")({ title: v })} testid="admin-fac-title" />
            </div>
            <TextArea label="Lead" value={sec("facilities").lead || ""} onChange={(v) => setSec("facilities")({ lead: v })} rows={2} testid="admin-fac-lead" />
            <ListEditor
              label="Facility Items"
              items={sec("facilities").items || []}
              onChange={(items) => setSec("facilities")({ items })}
              newItem={() => ({ name: "New Facility", desc: "Description" })}
              testid="admin-fac-items"
              renderItem={(f, patch) => (
                <div className="grid gap-4 md:grid-cols-2">
                  <TextInput label="Name" value={f.name} onChange={(v) => patch({ name: v })} testid="admin-fac-item-name" />
                  <TextArea label="Description" value={f.desc} onChange={(v) => patch({ desc: v })} rows={2} testid="admin-fac-item-desc" />
                </div>
              )}
            />
          </div>
        )}

        {tab === "Gallery" && (
          <div className="space-y-6">
            <div className="grid gap-5 md:grid-cols-2">
              <TextInput label="Eyebrow" value={sec("gallery").eyebrow || ""} onChange={(v) => setSec("gallery")({ eyebrow: v })} testid="admin-gal-eyebrow" />
              <TextInput label="Title" value={sec("gallery").title || ""} onChange={(v) => setSec("gallery")({ title: v })} testid="admin-gal-title" />
            </div>
            <TextArea label="Lead" value={sec("gallery").lead || ""} onChange={(v) => setSec("gallery")({ lead: v })} rows={2} testid="admin-gal-lead" />
            <ListEditor
              label="Gallery Images"
              items={sec("gallery").images || []}
              onChange={(images) => setSec("gallery")({ images })}
              newItem={() => ({ src: "", caption: "New Photo" })}
              testid="admin-gal-images"
              renderItem={(img, patch) => (
                <div className="grid gap-4 md:grid-cols-2">
                  <ImageInput label="Image" value={img.src} onChange={(v) => patch({ src: v })} testid="admin-gal-img" />
                  <TextInput label="Caption" value={img.caption} onChange={(v) => patch({ caption: v })} testid="admin-gal-caption" />
                </div>
              )}
            />
          </div>
        )}

        {tab === "Booking" && (
          <div className="grid gap-5 md:grid-cols-2">
            <TextInput label="Eyebrow" value={sec("booking").eyebrow || ""} onChange={(v) => setSec("booking")({ eyebrow: v })} testid="admin-bk-eyebrow" />
            <TextInput label="Title" value={sec("booking").title || ""} onChange={(v) => setSec("booking")({ title: v })} testid="admin-bk-title" />
            <div className="md:col-span-2">
              <TextArea label="Lead" value={sec("booking").lead || ""} onChange={(v) => setSec("booking")({ lead: v })} rows={2} testid="admin-bk-lead" />
            </div>
            <TextInput label="Side Panel — Tamil Line" value={sec("booking").sideTamil || ""} onChange={(v) => setSec("booking")({ sideTamil: v })} testid="admin-bk-tamil" />
            <TextInput label="Side Panel — Title" value={sec("booking").sideTitle || ""} onChange={(v) => setSec("booking")({ sideTitle: v })} testid="admin-bk-sidetitle" />
            <div className="md:col-span-2">
              <TextArea label="Side Panel — Body" value={sec("booking").sideBody || ""} onChange={(v) => setSec("booking")({ sideBody: v })} rows={2} testid="admin-bk-sidebody" />
            </div>
            <TextInput label="Phone" value={sec("booking").phone || ""} onChange={(v) => setSec("booking")({ phone: v })} testid="admin-bk-phone" />
            <TextInput label="Email" value={sec("booking").email || ""} onChange={(v) => setSec("booking")({ email: v })} testid="admin-bk-email" />
            <div className="md:col-span-2">
              <TextInput label="Notice Line" value={sec("booking").notice || ""} onChange={(v) => setSec("booking")({ notice: v })} testid="admin-bk-notice" />
            </div>
          </div>
        )}

        {tab === "Location & Contact" && (
          <div className="grid gap-5 md:grid-cols-2">
            <TextInput label="Eyebrow" value={sec("location").eyebrow || ""} onChange={(v) => setSec("location")({ eyebrow: v })} testid="admin-loc-eyebrow" />
            <TextInput label="Title" value={sec("location").title || ""} onChange={(v) => setSec("location")({ title: v })} testid="admin-loc-title" />
            <div className="md:col-span-2">
              <TextArea label="Address (line breaks preserved)" value={sec("location").address || ""} onChange={(v) => setSec("location")({ address: v })} rows={4} testid="admin-loc-address" />
            </div>
            <TextInput label="Phone" value={sec("location").phone || ""} onChange={(v) => setSec("location")({ phone: v })} testid="admin-loc-phone" />
            <TextInput label="Google Maps Link (Get Directions)" value={sec("location").mapsUrl || ""} onChange={(v) => setSec("location")({ mapsUrl: v })} testid="admin-loc-mapsurl" />
            <TextInput label="Email 1" value={sec("location").email1 || ""} onChange={(v) => setSec("location")({ email1: v })} testid="admin-loc-email1" />
            <TextInput label="Email 2" value={sec("location").email2 || ""} onChange={(v) => setSec("location")({ email2: v })} testid="admin-loc-email2" />
            <div className="md:col-span-2">
              <TextInput label="Map Pin (coordinates or address for the embedded map)" value={sec("location").mapQuery || ""} onChange={(v) => setSec("location")({ mapQuery: v })} testid="admin-loc-mapquery" />
            </div>
          </div>
        )}

        {tab === "Footer" && (
          <div className="grid gap-5">
            <TextArea label="Footer Tagline" value={sec("footer").tagline || ""} onChange={(v) => setSec("footer")({ tagline: v })} rows={2} testid="admin-footer-tagline" />
            <TextInput label="Domain Line" value={sec("footer").domain || ""} onChange={(v) => setSec("footer")({ domain: v })} testid="admin-footer-domain" />
            <p className="text-xs text-zinc-400">
              Header and footer brand names and the Tamil name come from Hero → Tamil Name.
            </p>
          </div>
        )}
      </div>

      <p className="mt-4 flex items-center gap-2 pb-6 text-xs text-zinc-400">
        Uploaded images are stored in cloud object storage; hero and card images can also use external URLs.
        {draft?.updated_at ? ` Last updated ${draft.updated_at}.` : ""}
        <img src={mediaUrl(draft?.hero?.image || "")} alt="" className="hidden" />
      </p>
    </div>
  );
};
